import { LocaleBundle } from "../types";

const pt: LocaleBundle = {
  "ui": {
    "nav": {
      "features": "Funcionalidades",
      "pricing": "Preços",
      "integrations": "Integrações",
      "industries": "Setores",
      "apiDocs": "Documentação",
      "about": "Sobre",
      "customers": "Clientes",
      "contact": "Contacto",
      "login": "Iniciar sessão",
      "getFreeCRM": "Obter FreeCRM",
      "openMenu": "Abrir menu",
      "language": "Idioma"
    },
    "hero": {
      "badge": "O CRM multicanal: e-mail, voz e SMS",
      "titleStart": "O CRM criado para ",
      "titleHighlight": "equipas de vendas multicanal",
      "subtitle": "Chegue a todos os clientes onde eles estiverem — com marketing por e-mail, voz e SMS integrados numa única plataforma. Sem extras, sem preços de empresa.",
      "cta": "Comece gratuitamente — não é necessário cartão",
      "pills": [
        "Marketing por e-mail",
        "Voz e chamadas",
        "SMS bidirecional"
      ],
      "iosApp": "iOS App",
      "androidApp": "Android App",
      "heroAlt": "Dashboard do FreeCRM a mostrar o pipeline de negócios e a gestão de contactos"
    },
    "logos": {
      "trustedBy": "Com a confiança de mais de 25.000 equipas em crescimento"
    },
    "features": {
      "eyebrow": "Funcionalidades",
      "title": "Um CRM. Todos os canais que os seus clientes usam.",
      "subtitle": "Marketing por e-mail, voz e SMS numa única plataforma — para que a sua equipa possa contactar potenciais clientes da forma que eles preferirem.",
      "items": [
        {
          "title": "Marketing por e-mail",
          "desc": "Crie campanhas, envie comunicações em massa e nutra leads com sequências — tudo a partir do mesmo registo de contacto."
        },
        {
          "title": "Voz e chamadas",
          "desc": "Click-to-call, registo e gravação de chamadas integrados. As chamadas recebidas estão incluídas com correio de voz programável — ligue o seu número e comece a conversar instantaneamente."
        },
        {
          "title": "SMS bidirecional",
          "desc": "Envie SMS a leads e clientes a partir da mesma caixa de entrada. Inclui modelos, envios agendados e histórico completo de conversas."
        },
        {
          "title": "Sequências multicanal",
          "desc": "Combine e-mail, SMS e chamadas numa única cadência. Alcance cada potencial cliente no canal ao qual ele realmente responde."
        },
        {
          "title": "Análise unificada",
          "desc": "Acompanhe aberturas, cliques, respostas, ligações e conversões em todos os canais num único dashboard."
        },
        {
          "title": "Automação",
          "desc": "Inscreva automaticamente leads em campanhas, distribua-os entre representantes e acione seguimentos com base em qualquer sinal."
        },
        {
          "title": "Agendamento de reuniões",
          "desc": "Partilhe links de reserva públicos com vários tipos de reunião. A sincronização do calendário mantém a disponibilidade atualizada e os links de videoconferência são gerados automaticamente para cada reunião."
        },
        {
          "title": "Integrações",
          "desc": "Ligue o Google, o Microsoft 365, o DocuSign, o QuickBooks, o Zapier e a videoconferência com Zoom, Google Meet e Zoho Meeting para manter os dados sincronizados e automatizar fluxos de trabalho nas ferramentas que já utiliza."
        },
        {
          "title": "CRM assistido por IA",
          "desc": "Deixe a IA redigir respostas de email, sugerir os próximos passos, resumir conversas e manter cada negócio em movimento — para que a sua equipa escreva menos e venda mais."
        }
      ]
    },
    "testimonials": {
      "eyebrow": "Clientes",
      "title": "Com a confiança de equipas de vendas multicanal",
      "items": [
        {
          "quote": "Substituímos três ferramentas — marketing por e-mail, marcador e CRM — pelo FreeCRM. A nossa equipa agora executa campanhas por e-mail, SMS e chamadas a partir de um único lugar.",
          "name": "Maya Chen",
          "role": "VP de Vendas, Velocity"
        },
        {
          "quote": "As sequências multicanal mudaram tudo. Combinar e-mail, SMS e uma chamada numa única cadência duplicou a nossa taxa de resposta sem aumentar a equipa.",
          "name": "Daniel Okafor",
          "role": "Diretor de Vendas, Apex Solutions"
        },
        {
          "quote": "Mudámos do HubSpot para o FreeCRM e finalmente conseguimos marketing por e-mail, voz e SMS numa única plataforma — com zero custos de integração.",
          "name": "Priya Raman",
          "role": "COO, Brightline"
        }
      ]
    },
    "pricing": {
      "eyebrow": "Preços",
      "title": "Preços do software de CRM",
      "subtitle": "Números de telefone dos EUA por apenas $1.15/mês · $0.01 por minuto · $0.01 por SMS",
      "mostPopular": "Mais popular",
      "perUserMo": "/utilizador/mês",
      "tiers": [
        {
          "name": "Gratuito",
          "price": "$0",
          "blurb": "Gratuito para sempre com e-mail, voz e SMS integrados.",
          "cta": "Registe-se gratuitamente"
        },
        {
          "name": "Pro",
          "price": "$10",
          "blurb": "Todo o poder do CRM para equipas de vendas em crescimento.",
          "cta": "Registar"
        },
        {
          "name": "Enterprise",
          "price": "$25",
          "blurb": "Funcionalidades avançadas para equipas com grande volume.",
          "cta": "Registar"
        }
      ],
      "sharedFeatures": [
        "Armazenamento ilimitado",
        "Contactos e empresas",
        "Negócios e tarefas",
        "Casos e chamadas",
        "Calendário e documentos",
        "Relatórios",
        "Aplicações iOS e Android",
        "Campanhas e formulários",
        "E-mail",
        "Acesso à API",
        "Sincronização com Google",
        "QuickBooks",
        "SMS",
        "Chamadas de voz",
        "Cartão de crédito"
      ]
    },
    "faq": {
      "eyebrow": "Perguntas Frequentes",
      "title": "Perguntas, respondidas",
      "items": [
        {
          "q": "O FreeCRM é realmente gratuito para e-mail, voz e SMS?",
          "a": "Sim — o plano Gratuito inclui marketing por e-mail, click-to-call, registo de chamadas e 500 mensagens SMS por mês, sem custos. Os planos pagos desbloqueiam limites de envio maiores, automação avançada, gravação de chamadas e SMS ilimitados."
        },
        {
          "q": "Qual a diferença entre o FreeCRM e o HubSpot?",
          "a": "Ao contrário do HubSpot, todos os canais são nativos — marketing por e-mail, voz e SMS estão todos incluídos, não são vendidos como extras separados ou integrações dispendiosas."
        },
        {
          "q": "Posso executar campanhas e sequências de e-mail?",
          "a": "Sim. Crie comunicações em massa, campanhas de gotejamento e sequências de vários passos que combinam e-mail, SMS e chamadas — com acompanhamento completo de aberturas, cliques e respostas."
        },
        {
          "q": "Como funciona a telefonia no FreeCRM?",
          "a": "As chamadas funcionam diretamente no seu navegador — não é necessário um marcador ou telefone separado. Faça chamadas de saída com click-to-call, receba chamadas de entrada no seu número FreeCRM e encaminhe-as para o representante correto. O correio de voz programável com saudações personalizadas e transcrição automática captura todas as chamadas perdidas."
        },
        {
          "q": "Suportam SMS bidirecional?",
          "a": "Sim. Envie e receba SMS a partir da mesma caixa de entrada que o seu e-mail, com modelos, envios agendados e histórico completo de conversas por contacto."
        }
      ]
    },
    "cta": {
      "title1": "E-mail, voz e SMS num único CRM.",
      "title2": "Alcance clientes em todos os canais.",
      "subtitle": "Junte-se a mais de 25.000 equipas que usam o FreeCRM para marketing por e-mail, voz e SMS — tudo numa única plataforma multicanal gratuita.",
      "button": "Comece gratuitamente"
    },
    "footer": {
      "tagline": "O CRM gratuito que cresce com o seu negócio.",
      "copyright": "Todos os direitos reservados.",
      "tagBuiltFor": "Criado para equipas que merecem melhor que o HubSpot.",
      "cols": {
        "product": "Produto",
        "company": "Empresa",
        "resources": "Recursos",
        "legal": "Legal"
      },
      "links": {
        "features": "Funcionalidades",
        "pricing": "Preços",
        "integrations": "Integrações",
        "industries": "Setores",
        "changelog": "Registo de alterações",
        "about": "Sobre",
        "customers": "Clientes",
        "careers": "Carreiras",
        "contact": "Contacto",
        "blog": "Blogue",
        "helpCenter": "Centro de Ajuda",
        "apiDocs": "Documentação",
        "community": "Comunidade",
        "privacy": "Privacidade",
        "terms": "Termos",
        "security": "Segurança",
        "dpa": "DPA"
      }
    },
    "contentPage": {
      "readyToStart": "Pronto para começar?",
      "joinThousands": "Junte-se a milhares de equipas que usam o FreeCRM para potenciar marketing por e-mail, voz, SMS e o seu pipeline.",
      "defaultCta": "Comece gratuitamente — não é necessário cartão",
      "learnMore": "Saiba mais →"
    },
    "languageNames": {
      "en": "Inglês",
      "es": "Espanhol",
      "fr": "Francês",
      "pt": "Português",
      "it": "Italiano", "de": "Deutsch", "ar": "العربية"
    }
  },
  "featurePages": {
    "calling": {
      "title": "Chamadas integradas",
      "eyebrow": "Funcionalidade",
      "description": "Click-to-call, chamadas recebidas com correio de voz programável, chamadas no navegador, números virtuais, gravação e o power dialer — tudo integrado no FreeCRM.",
      "intro": "As soluções de CRM para call centers da FreeCRM automatizam as chamadas de entrada e saída em 90 países. As chamadas recebidas estão incluídas com correio de voz programável — obtenha um número virtual ou use o seu próprio telemóvel, e comece a marcar a partir de qualquer registo de contacto.",
      "sections": [
        {
          "heading": "Click-to-call a partir de qualquer lugar",
          "body": "Veja por que somos o CRM gratuito n.º 1 para automação da força de vendas, marketing e apoio ao cliente — com chamadas click-to-call no navegador para qualquer número de telefone, a partir de qualquer contacto, negócio ou registo de empresa."
        },
        {
          "heading": "Gravação, agendamento e chamadas em grupo",
          "body": "Grave chamadas para fins de formação e conformidade, agende seguimentos, realize chamadas em grupo e envie SMS — tudo a partir do CRM, sem ferramentas extra."
        },
        {
          "heading": "Chamadas recebidas e correio de voz programável",
          "body": "Cada número FreeCRM recebe chamadas de entrada e saída — sem custos adicionais. Encaminhe as chamadas para o representante certo, defina horários comerciais e configure um correio de voz programável com saudações personalizadas e transcrição automática para que nenhuma mensagem se perca."
        },
        {
          "heading": "Power dialer e scripts de chamada",
          "body": "Maximize a eficácia com clientes existentes e potenciais. Scripts de chamada e automação poderosos geram resultados de vendas fiáveis, ajudando-o a aumentar os seus lucros."
        },
        {
          "heading": "Campanhas e seguimentos",
          "body": "Crie campanhas de chamadas e SMS, automatize chamadas e SMS, e organize e agende os seus seguimentos para que nada fique esquecido."
        }
      ]
    },
    "contacts": {
      "title": "Contactos e gestão de leads",
      "eyebrow": "Funcionalidade",
      "description": "Contactos ilimitados, campos personalizados e importação poderosa do Outlook, Salesforce, HubSpot ou Zoho.",
      "intro": "Os contactos são os seus potenciais clientes, leads e clientes — fornecedores, parceiros, doadores. No FreeCRM, os seus contactos são ouro e nós tratamo-los como tal.",
      "sections": [
        {
          "heading": "Personalize tudo",
          "body": "Adicione quantos campos personalizados precisar. Todas as empresas precisam de um CRM personalizado — com o FreeCRM, pode personalizar fichas de contacto, formulários e todas as partes do sistema."
        },
        {
          "heading": "Acompanhe todas as interações",
          "body": "Acompanhe e gira relacionamentos, chamadas e reuniões. Associe documentos, notas de reunião e de chamada e outras informações importantes às pessoas para quem e com quem trabalha."
        },
        {
          "heading": "B2B, B2C e mais",
          "body": "Os contactos são indivíduos que podem pertencer a empresas ou a outras organizações. Damos suporte a modelos de negócio B2B, B2C e mais complexos."
        },
        {
          "heading": "Importe de qualquer lugar",
          "body": "Importe listas de contactos de qualquer ficheiro de folha de cálculo, Outlook, Salesforce, HubSpot, Zoho ou qualquer outro software de gestão de contactos."
        }
      ]
    },
    "companies": {
      "title": "Empresas e contas",
      "eyebrow": "Funcionalidade",
      "description": "Acompanhe organizações, fornecedores e parceiros com campos personalizados, listas e histórico de atividades partilhado.",
      "intro": "A lista de Empresas do FreeCRM oferece um acompanhamento organizacional robusto e flexível para qualquer entidade — B2B, B2C, sem fins lucrativos ou governamental — com campos completamente personalizáveis.",
      "sections": [
        {
          "heading": "Organize e partilhe",
          "body": "Organize por categoria, estado, prioridade ou filtros personalizados. Importe e exporte listas instantaneamente, crie listas ilimitadas e partilhe informações organizacionais com a sua equipa."
        },
        {
          "heading": "Toda a atividade num só lugar",
          "body": "Chamadas de vendas, tickets de problemas e atividades de clientes são acompanhados e relatados. As campanhas de e-mail e o acompanhamento da automação de chamadas são organizados por empresa."
        },
        {
          "heading": "Porquê empresas?",
          "body": "Ao acompanhar as empresas com as quais faz negócios, as suas equipas podem orquestrar campanhas de vendas mais eficazes e identificar e partilhar oportunidades em toda a sua organização."
        }
      ]
    },
    "deals": {
      "title": "Pipeline de negócios",
      "eyebrow": "Funcionalidade",
      "description": "Pipeline de vendas visual, acompanhamento de fases, previsões e relatórios — criado para equipas de vendas outbound.",
      "intro": "Os negócios são a razão pela qual está aqui. O conjunto de funcionalidades de Negócios do FreeCRM permite-lhe gerir e acompanhar leads, aplicando uma metodologia de vendas sólida para levar o seu negócio ao próximo nível.",
      "sections": [
        {
          "heading": "Acompanhamento de fases",
          "body": "Os negócios são acompanhados por estado — cotação, orçamento, ordem de compra, fatura, comissão — para que saiba sempre em que ponto está cada oportunidade."
        },
        {
          "heading": "Relatórios e previsões",
          "body": "O pipeline fornece tabelas, relatórios e gráficos que mostram o desempenho da sua equipa de vendas e das suas campanhas. Quantifique o sucesso ao longo do tempo e identifique novas oportunidades com clientes novos ou existentes."
        },
        {
          "heading": "Colaboração em equipa",
          "body": "O acompanhamento de negócios traz coesão às pessoas que apoiam as suas vendas. As equipas de serviços e suporte podem colaborar ao longo do ciclo para demonstrar e proporcionar o sucesso da equipa."
        }
      ]
    },
    "tasks": {
      "title": "Gestão de tarefas",
      "eyebrow": "Funcionalidade",
      "description": "Priorize, atribua e acompanhe tarefas com fluxos de trabalho e estados — em negócios, contactos e casos.",
      "intro": "O acompanhamento de tarefas, problemas e vendas dá-lhe visibilidade completa sobre o tempo que você e a sua equipa dedicam a cada negócio ou problema.",
      "sections": [
        {
          "heading": "Crie uma tarefa!",
          "body": "Priorize tarefas, crie tipos de tarefa personalizados específicos para o seu negócio, atribua tarefas a membros da equipa ou funcionários, defina-lhes fluxos de trabalho e estados, e delegue a carga de trabalho pela sua equipa."
        },
        {
          "heading": "Um melhor delegador",
          "body": "Como pode atribuir tarefas, torna-se instantaneamente um melhor delegador sem perder a visibilidade sobre a qualidade e o serviço que a sua equipa oferece."
        }
      ]
    },
    "cases": {
      "title": "Gestão de casos",
      "eyebrow": "Funcionalidade",
      "description": "Abra tickets, encaminhe problemas, personalize estados e prioridades, e resolva cada caso de cliente.",
      "intro": "O acompanhamento de casos e problemas oferece 100% de visibilidade sobre os problemas que resolve para clientes, fornecedores e parceiros.",
      "sections": [
        {
          "heading": "Help desk virtual",
          "body": "Inicie um novo caso e abra um ticket no seu help desk virtual. Associe casos de longo prazo a problemas contínuos que podem ser resolvidos e fechados rapidamente."
        },
        {
          "heading": "Personalize para a sua equipa",
          "body": "Personalize os problemas de suporte com campos personalizados, atribua problemas a um ou vários membros da equipa e acompanhe cada caso para que nenhum problema de cliente fique esquecido."
        },
        {
          "heading": "Conecte tudo",
          "body": "Relacione casos com empresas, contactos, chamadas, eventos ou documentos. Adicione notas à medida que o problema avança e mantenha toda a informação num caso conveniente."
        }
      ]
    },
    "campaigns": {
      "title": "Automação de marketing",
      "eyebrow": "Funcionalidade",
      "description": "Campanhas de e-mail e SMS, segmentação e automação integradas no seu CRM.",
      "intro": "Construa listas de correio com base em dados demográficos, preferências e interações. Crie e execute campanhas de e-mail personalizadas em quatro passos simples.",
      "sections": [
        {
          "heading": "Personalizado em escala",
          "body": "O software de marketing por e-mail do CRM faz mais do que enviar e-mails em massa — construa sequências personalizadas, gira listas segmentadas e faça seguimentos automaticamente."
        },
        {
          "heading": "Modelos adaptados a dispositivos móveis",
          "body": "Modelos de e-mail facilmente personalizáveis que são responsivos em todos os dispositivos, incluindo telemóveis, tablets e muito mais."
        },
        {
          "heading": "SMS + e-mail juntos",
          "body": "Combine sequências de e-mail e SMS. Interaja com os seus clientes como nunca antes e mantenha tudo num só lugar."
        }
      ]
    },
    "calendar": {
      "title": "Calendário de grupo",
      "eyebrow": "Funcionalidade",
      "description": "Calendário de equipa partilhado com lembretes por e-mail e SMS, totalmente integrado com contactos, negócios e tarefas.",
      "intro": "O calendário de grupo do FreeCRM é uma ótima forma de gerir e visualizar o seu horário, as atividades diárias, semanais e mensais da sua equipa, as reuniões com clientes e as chamadas.",
      "sections": [
        {
          "heading": "Nunca perca um evento",
          "body": "Envie notificações por e-mail e SMS para que ninguém tenha uma desculpa para faltar a um evento novamente."
        },
        {
          "heading": "Conectado a tudo",
          "body": "Acompanhe os próximos eventos, defina lembretes, crie tarefas, insira notas, anexe ficheiros e associe entradas de calendário a um ou mais contactos. Atribua tarefas a um ou mais colegas de trabalho."
        },
        {
          "heading": "Filtre por equipa ou contacto",
          "body": "Visualize eventos para toda a base de dados, um único contacto, para si mesmo, um colega de trabalho ou vários membros da equipa. Está sempre a um clique de distância do histórico ou das informações de contacto."
        }
      ]
    },
    "documents": {
      "title": "Gestão de documentos",
      "eyebrow": "Funcionalidade",
      "description": "Armazenamento ilimitado de documentos, acesso móvel e anexos de ficheiros por registo.",
      "intro": "Partilhe documentos importantes com a sua equipa e forneça acesso instantâneo a todos os seus ficheiros eletrónicos. Carregue um único ficheiro ou um diretório inteiro com um clique.",
      "sections": [
        {
          "heading": "Armazenamento ilimitado",
          "body": "O nosso serviço gratuito inclui armazenamento ilimitado para todos os documentos importantes da sua equipa. Cada membro pode ter o seu próprio arquivo de documentos e associar ficheiros a qualquer registo."
        },
        {
          "heading": "Mobile-first",
          "body": "Aceda a documentos a partir das nossas aplicações iOS e Android — abra o CRM móvel, consulte um documento e envie SMS e e-mails a potenciais clientes no momento."
        },
        {
          "heading": "Centralize os ativos",
          "body": "Um lugar seguro e protegido para centralizar os seus ativos de documentos eletrónicos. A sua equipa pode aceder a contratos, literatura de vendas e marketing, formulários padronizados e muito mais, a partir de qualquer lugar do mundo."
        }
      ]
    },
    "email": {
      "title": "Automação de e-mail",
      "eyebrow": "Funcionalidade",
      "description": "Sequências de e-mail personalizadas, listas segmentadas e segmentação orientada por CRM.",
      "intro": "Construa listas de correio com base em dados demográficos, preferências e interações para mensagens direcionadas. Poupe tempo e aumente a produtividade em 74% com as ferramentas de e-mail certas.",
      "sections": [
        {
          "heading": "Quatro passos simples",
          "body": "Crie e execute campanhas de e-mail personalizadas em quatro passos simples com o software de marketing por e-mail do CRM."
        },
        {
          "heading": "Mais do que envios em massa",
          "body": "O marketing por e-mail do CRM ajuda as equipas com muito mais do que apenas o seguimento de leads de vendas, o envio de e-mails em massa ou a criação de sequências personalizadas."
        },
        {
          "heading": "Modelos responsivos",
          "body": "Modelos facilmente personalizáveis que são responsivos em todos os dispositivos — telemóveis, tablets e computadores de secretária."
        }
      ]
    },
    "forms": {
      "title": "Formulários e inquéritos",
      "eyebrow": "Funcionalidade",
      "description": "Formulários de assistente de várias páginas, scripts de chamada e inquéritos que alimentam diretamente o CRM.",
      "intro": "Os formulários são inquéritos de perguntas e respostas — e também podem ser scripts de chamada que a sua equipa de vendas ou suporte preenche com os potenciais clientes ao telefone.",
      "sections": [
        {
          "heading": "Formulários de assistente",
          "body": "A criação de formulários e inquéritos é fácil com o nosso criador de formulários, que cria Formulários de Assistente de várias páginas fáceis de seguir e preencher. Uma vez concluído, receberá um link público para distribuir."
        },
        {
          "heading": "Feedback de clientes e scripts internos",
          "body": "Use formulários para feedback de clientes, questionários para funcionários e scripts de chamada. Crie inquéritos personalizados para vendas, marketing e satisfação do cliente."
        },
        {
          "heading": "Relatórios e exportação",
          "body": "Os resultados dos formulários web e inquéritos alimentam um sistema de relatórios, apresentados como tabelas. Exporte para Excel ou CSV para análise ou importação para marketing."
        }
      ]
    }
  },
  "industryPages": {
    "callcenter": {
      "title": "FreeCRM para call centers",
      "eyebrow": "Setor",
      "description": "CRM para call centers com marcação integrada, gravação, scripts e SMS em 90 países.",
      "intro": "Automatize as chamadas de entrada e saída em 90 países em todo o mundo. Obtenha um número de telefone virtual e ative funcionalidades de call center desde o início.",
      "sections": [
        {
          "heading": "Todas as suas chamadas, num só lugar",
          "body": "Chamadas click-to-call no navegador para qualquer número de telefone. Obtenha um número virtual ou use o seu próprio telemóvel — o FreeCRM faz tudo."
        },
        {
          "heading": "Gravação e agendamento",
          "body": "Faça gravações de chamadas, agende chamadas e chamadas em grupo, e envie SMS a partir de qualquer dispositivo. Funcionalidades avançadas para automação de call center."
        },
        {
          "heading": "Scripts e automação",
          "body": "Scripts de chamada e automação poderosos geram resultados de vendas fiáveis. Crie scripts atribuídos e agendados para membros da equipa; acompanhe cada chamada e os seus resultados no nosso sistema rico em funcionalidades."
        },
        {
          "heading": "Campanhas e seguimento",
          "body": "Crie campanhas de chamadas e SMS, automatize-as e organize seguimentos para que a sua equipa nunca perca um ponto de contacto."
        }
      ]
    },
    "insurance": {
      "title": "FreeCRM para seguros",
      "eyebrow": "Setor",
      "description": "CRM na nuvem para seguradoras, para agentes, angariadores e serviços acompanharem clientes, apólices e sinistros.",
      "intro": "O CRM para o setor de seguros ajuda agentes, angariadores e serviços a vender a consumidores e empresas e a acompanhar todas as interações na nuvem.",
      "sections": [
        {
          "heading": "Criado para agentes e subscritores",
          "body": "Quer seja um agente, subscritor, prestador de serviços ou outro profissional de seguros, podemos ajudá-lo com as suas necessidades de CRM."
        },
        {
          "heading": "Apólices, sinistros e clientes",
          "body": "Automatize o acompanhamento de clientes importantes, apólices, sinistros e muito mais com modelos de seguros prontos a usar que funcionam para B2C e B2B."
        },
        {
          "heading": "Criado para a retenção",
          "body": "O CRM é fundamental no setor de seguros devido à complexidade dos produtos, à necessidade de comunicação contínua e à importância da retenção de clientes."
        }
      ]
    },
    "realestate": {
      "title": "FreeCRM para o setor imobiliário",
      "eyebrow": "Setor",
      "description": "Gestão de leads e propriedades para profissionais do setor imobiliário residencial e comercial.",
      "intro": "O FreeCRM é usado em todo o setor imobiliário por todas as classes de profissionais — residenciais ou comerciais, na compra, venda, mediação ou gestão.",
      "sections": [
        {
          "heading": "Gestão de leads e propriedades",
          "body": "Otimize a gestão de leads e a gestão de propriedades no setor residencial e comercial. Os agentes adoram o nosso CRM — e os mediadores e gestores de propriedades também."
        },
        {
          "heading": "Liste propriedades como negócios",
          "body": "Use a secção de Negócios para listar propriedades — geralmente por morada (por exemplo, 'Rua Principal, 100'). Adicione vários potenciais clientes a uma única propriedade e use o quadro de vendas ou o pipeline para acompanhar os funis."
        },
        {
          "heading": "HOAs e ativos complexos",
          "body": "Gestores de propriedades em condomínios residenciais, comerciais, multifamiliares, arranha-céus e HOAs usam o FreeCRM para gerir todos os aspetos das operações."
        }
      ]
    },
    "dental": {
      "title": "FreeCRM para o setor dentário",
      "eyebrow": "Setor",
      "description": "CRM preparado para HIPAA para dentistas, cirurgiões orais e a comunidade dentária especializada.",
      "intro": "Apoiamos o setor dentário com um CRM de ponta para dentistas, cirurgiões orais e todos na comunidade médica especializada — e fornecemos certificação BAA para conformidade com a HIPAA.",
      "sections": [
        {
          "heading": "Com a confiança de milhares de clínicas",
          "body": "Com milhares de clientes no setor dentário, o FreeCRM lida com os requisitos de conformidade da odontologia, cirurgia oral, implantes dentários e setores adjacentes que manuseiam informações de pacientes."
        },
        {
          "heading": "Segurança de dados",
          "body": "A segurança de dados é a essência do FreeCRM. Os servidores estão alojados na AWS — em conformidade com a HIPAA — e fornecemos certificados BAA para as clínicas que precisam deles."
        },
        {
          "heading": "Integre a sua stack",
          "body": "Comece rapidamente e integre com os principais sistemas de gestão de clínicas e de imagem usados em toda a odontologia."
        }
      ]
    },
    "hipaa": {
      "title": "CRM em conformidade com a HIPAA",
      "eyebrow": "Conformidade",
      "description": "O FreeCRM está em conformidade com a HIPAA há mais de uma década — certificados BAA disponíveis com o plano Enterprise.",
      "intro": "Gerir informações de saúde de pacientes é um desafio. A FreeCRM lançou o HIPAACRM.com em 2014 — o primeiro CRM do mundo em conformidade com a HIPAA — e continua a ser líder em segurança e responsabilidade.",
      "sections": [
        {
          "heading": "Precisa de um BAA?",
          "body": "Se precisa de um Adendo de Associado de Negócios (BAA) para garantir a conformidade com a HIPAA, podemos ajudar. É necessária a nossa edição Enterprise, paga anualmente por utilizador antecipadamente ($300/utilizador/ano)."
        },
        {
          "heading": "Além das clínicas médicas",
          "body": "Não são apenas as clínicas médicas — ensaios clínicos, nutricionistas, terapeutas e qualquer negócio que lide com informações de saúde aproveitam o nosso suporte HIPAA."
        },
        {
          "heading": "Segurança de ponta",
          "body": "12 anos de experiência em HIPAA, alojado na AWS, com a segurança e responsabilidade de que precisa para informações de saúde protegidas."
        }
      ]
    },
    "insidesales": {
      "title": "FreeCRM para vendas internas e independentes",
      "eyebrow": "Setor",
      "description": "Aumente a produtividade em mais de 30% com automação de CRM simples, criada para representantes internos e independentes.",
      "intro": "O FreeCRM ajuda representantes de vendas independentes e equipas de vendas internas a aumentar a produtividade com ferramentas e dashboards de automação de CRM simples.",
      "sections": [
        {
          "heading": "Mais de 30% de produtividade",
          "body": "Representantes de vendas independentes podem aumentar as vendas em mais de 30% apenas acompanhando contactos e empresas — e as nossas ferramentas de automação amplificam o impacto."
        },
        {
          "heading": "Acompanhe todas as interações",
          "body": "Registe todas as chamadas, e-mails e reuniões. Tenha um registo claro do que foi dito e do que precisa de acontecer a seguir, priorize tarefas e faça o seguimento a tempo."
        },
        {
          "heading": "Amigável para trabalhadores independentes",
          "body": "Criado para a autonomia de representantes independentes que representam várias empresas. Gira o seu próprio horário e as relações com os clientes a partir de qualquer lugar."
        }
      ]
    },
    "contractor": {
      "title": "FreeCRM para empreiteiros e construtores",
      "eyebrow": "Setor",
      "description": "Gira negócios, subempreiteiros, agendamentos e alertas em tempo real para GCs e construtores comerciais.",
      "intro": "Empreiteiros, construtores, GCs e fornecedores de construção comercial usam facilmente o FreeCRM para gerir negócios e múltiplos terceiros, como subempreiteiros.",
      "sections": [
        {
          "heading": "Gestão de leads",
          "body": "Acompanhe leads, monitorize o progresso e priorize esforços. Obtenha insights sobre o comportamento dos leads para poder decidir quais perseguir."
        },
        {
          "heading": "Orçamentos e propostas",
          "body": "Automatize o processo de orçamentação e propostas com uma base de dados centralizada de propostas, orçamentos e dados de preços anteriores — poupando tempo e melhorando a precisão."
        },
        {
          "heading": "Agendamento e alertas",
          "body": "Atribua rapidamente equipas e indivíduos com alertas em tempo real por e-mail ou SMS. Mantenha subempreiteiros e equipas coordenadas."
        }
      ]
    },
    "professional": {
      "title": "FreeCRM para serviços profissionais",
      "eyebrow": "Setor",
      "description": "Gestão de clientes, conformidade e prestação de serviços para empresas de serviços profissionais.",
      "intro": "As empresas de serviços profissionais usam o FreeCRM para gerir dados de clientes, acompanhar interações e prestar um melhor serviço a partir de qualquer lugar, em qualquer dispositivo.",
      "sections": [
        {
          "heading": "Melhoria da satisfação do cliente",
          "body": "Uma visão completa do relacionamento com o cliente — detalhes de contacto, transações passadas, preferências de comunicação — leva a uma maior satisfação e lealdade."
        },
        {
          "heading": "Conformidade mais fácil",
          "body": "Cumpra os requisitos do GDPR e leis semelhantes com um local central para armazenar e partilhar documentação de conformidade, e transparência nas fontes de dados dos clientes."
        },
        {
          "heading": "Fazer a empresa crescer",
          "body": "Hoje em dia, o CRM deve ser acessível de qualquer lugar, em qualquer dispositivo, em qualquer idioma — com inteligência social e de melhores práticas integrada para o ajudar a mover-se mais rapidamente."
        }
      ]
    }
  },
  "integrationPages": {
    "google": {
      "title": "FreeCRM + Google Workspace",
      "eyebrow": "Integração",
      "description": "Sincronize o Gmail, o Google Calendar e os Google Contacts com o FreeCRM num clique.",
      "intro": "O Gmail e o Google Workspace estão profundamente integrados com o FreeCRM. Conecte a sua conta Google e sincronize e-mail, contactos e calendário.",
      "sections": [
        {
          "heading": "Configuração num clique",
          "body": "É simples adicionar a sua conta Google — tal como qualquer outra aplicação integrada com o Google, é um início de sessão com um clique. Também pode usar a sua conta Google para iniciar sessão no FreeCRM."
        },
        {
          "heading": "Sincronize o que quiser",
          "body": "Depois de adicionar o Google ao FreeCRM, escolha se quer sincronizar a conta completa ou apenas usar a funcionalidade de e-mail integrada. O Calendário e os Google Contacts são sincronizados juntamente."
        },
        {
          "heading": "O Workspace torna-se um CRM",
          "body": "Sistemas de CRM altamente integrados podem transformar o Google Workspace num poderoso CRM que funciona com o alojamento empresarial do Google ou com qualquer conta Gmail."
        }
      ]
    },
    "quickbooks": {
      "title": "FreeCRM + QuickBooks",
      "eyebrow": "Integração",
      "description": "A integração de CRM com QuickBooks n.º 1 — Online e Desktop, sincronização bidirecional.",
      "intro": "A melhor integração com o QuickBooks — classificada como n.º 1 pelos QuickBooks ProAdvisors. O CRM para QuickBooks Online e Desktop simplifica as operações e ajuda-o a aumentar as vendas.",
      "sections": [
        {
          "heading": "Fortemente integrado",
          "body": "Se procura uma aplicação de CRM fortemente integrada com o QuickBooks Online, o FreeCRM é a solução perfeita."
        },
        {
          "heading": "Centralize os dados dos clientes",
          "body": "Um CRM que se integra com o QuickBooks utiliza integrações de software de contabilidade para armazenar e centralizar os dados dos seus clientes entre as vendas e as finanças."
        },
        {
          "heading": "Online + Desktop",
          "body": "Tanto o QuickBooks Online como o QuickBooks Desktop são suportados."
        }
      ]
    },
    "zapier": {
      "title": "FreeCRM + Zapier",
      "eyebrow": "Integração",
      "description": "Conecte o FreeCRM a mais de 6.000 aplicações no Zapier — a forma mais fácil de automatizar.",
      "intro": "Conecte instantaneamente o conector FreeCRM com as aplicações que usa todos os dias. O FreeCRM integra-se com outras 6.000 aplicações no Zapier — a forma mais fácil de automatizar.",
      "sections": [
        {
          "heading": "Mais de 6.000 aplicações",
          "body": "Do Slack ao Stripe e ao Google Sheets, envie e receba dados do FreeCRM para o resto da sua stack sem escrever código."
        },
        {
          "heading": "Automação sem código",
          "body": "Crie Zaps para registar chamadas, criar contactos, sincronizar negócios e acionar campanhas sempre que algo acontece nas suas outras ferramentas."
        }
      ]
    },
    "docusign": {
      "title": "FreeCRM + DocuSign",
      "eyebrow": "Integração",
      "description": "Envie, assine e acompanhe acordos diretamente do seu CRM com a integração DocuSign.",
      "intro": "O DocuSign é a plataforma líder mundial para assinar documentos. O FreeCRM permite-lhe otimizar processos preenchendo documentos com dados do CRM e atualizando o CRM a partir de campos do DocuSign.",
      "sections": [
        {
          "heading": "Acompanhamento em tempo real",
          "body": "Solicite assinaturas e acompanhe o estado dos acordos em tempo real. Poupe tempo com fluxos de trabalho personalizados que acionam acordos com o clique de um botão — e reduza erros."
        },
        {
          "heading": "Fluxo de dados bidirecional",
          "body": "Automatize fluxos de trabalho inteiros, gere e envie contratos, e arquive documentos concluídos. Atualize sistemas com dados de documentos ou preencha documentos a partir do CRM — funciona nos dois sentidos."
        }
      ]
    },
    "whatsapp": {
      "title": "FreeCRM + WhatsApp",
      "eyebrow": "Integração",
      "description": "Use a API do WhatsApp Business para chamadas e mensagens de texto a partir do FreeCRM.",
      "intro": "O WhatsApp e o FreeCRM formam uma combinação poderosa — use a API do WhatsApp Business para chamadas e mensagens, e automatize a captura de leads e as comunicações.",
      "sections": [
        {
          "heading": "Comunicação otimizada",
          "body": "Gira as interações com os clientes no WhatsApp num local central para tempos de resposta mais rápidos e maior satisfação."
        },
        {
          "heading": "Integração de dados de clientes",
          "body": "Traga os dados dos clientes do WhatsApp para o FreeCRM e automatize tarefas repetitivas como o reencaminhamento de mensagens, a priorização de respostas e a inserção de dados."
        }
      ]
    },
    "microsoft365": {
      "title": "FreeCRM + Microsoft 365",
      "eyebrow": "Integração",
      "description": "Sincronize o Outlook, o calendário e os contactos com o FreeCRM usando o Microsoft 365.",
      "intro": "O Microsoft 365 integra-se com o FreeCRM para que possa sincronizar o e-mail do Outlook, o calendário e os contactos num só lugar.",
      "sections": [
        {
          "heading": "Início de sessão Microsoft com um clique",
          "body": "Ligue a sua conta Microsoft 365 e use-a para iniciar sessão no FreeCRM, tal como qualquer outra aplicação integrada com a Microsoft."
        },
        {
          "heading": "E-mail, calendário e contactos",
          "body": "Escolha o que sincronizar — as conversações de e-mail, os eventos do calendário e os contactos do Outlook mantêm-se atualizados entre o FreeCRM e o Microsoft 365."
        },
        {
          "heading": "Pronto para equipas",
          "body": "Funciona com o Outlook pessoal, Microsoft 365 Business e inquilinos Enterprise para que toda a sua equipa permaneça sincronizada."
        }
      ]
    },
    "video-conferencing": {
      "title": "FreeCRM + Videoconferência",
      "eyebrow": "Integração",
      "description": "Gere links do Zoom, Google Meet e Zoho Meeting automaticamente para cada reunião agendada.",
      "intro": "O agendamento do FreeCRM cria automaticamente links de videoconferência para cada reunião. Ligue o Zoom, o Google Meet e o Zoho Meeting para que clientes e prospects entrem com um clique.",
      "sections": [
        {
          "heading": "Links de reunião automáticos",
          "body": "Quando alguém marca uma reunião através do seu link do FreeCRM, é gerado um link do Zoom, Google Meet ou Zoho Meeting e inserido automaticamente no convite do calendário."
        },
        {
          "heading": "Vários fornecedores",
          "body": "Use o fornecedor que a sua equipa já paga — Zoom, Google Meet ou Zoho Meeting — ou deixe os participantes escolherem."
        },
        {
          "heading": "Calendário unificado",
          "body": "Os detalhes da reunião, links e gravações permanecem anexados ao registo de contacto, negócio ou caso para que tudo fique num só lugar."
        }
      ]
    },
    "aws": {
      "title": "FreeCRM na AWS",
      "eyebrow": "Infraestrutura",
      "description": "Disponibilidade de seis noves, engenheiros 24/7/365 e segurança de dados de nível federal na Amazon AWS.",
      "intro": "O FreeCRM opera na Amazon AWS com um objetivo de disponibilidade de seis noves (99.9999%). Os engenheiros estão de serviço 24/7/365.",
      "sections": [
        {
          "heading": "Instalações de nível federal",
          "body": "As nossas instalações físicas cumprem ou excedem todos os requisitos federais e internacionais de segurança de dados, redundância, recuperação de negócios e continuidade."
        },
        {
          "heading": "Nuvem da mais alta qualidade",
          "body": "Confie em nós para fornecer os serviços de nuvem da mais alta qualidade disponíveis em qualquer lugar — encriptados, monitorizados e continuamente disponíveis."
        }
      ]
    }
  },
  "footerPages": {
    "features": {
      "title": "Tudo o que precisa num só CRM",
      "eyebrow": "Funcionalidades",
      "description": "Chamadas integradas, SMS, negócios, contactos, tarefas, casos, campanhas, calendário, documentos, relatórios, API e integrações — gratuito para sempre.",
      "intro": "O FreeCRM oferece-lhe contactos, empresas, negócios, tarefas, casos, chamadas, calendário, documentos, relatórios, campanhas, formulários, e-mail, API e integrações — tudo num só lugar, gratuito para sempre."
    },
    "pricing": {
      "title": "Preços",
      "description": "CRM gratuito para sempre. Pro a $10/utilizador/mês. Enterprise a $25/utilizador/mês. Números dos EUA a $1.15/mês, $0.01/min, $0.01/SMS."
    },
    "integrations": {
      "title": "Integrações e aplicações conectadas",
      "eyebrow": "Integrações",
      "description": "Conecte o FreeCRM ao Google, Microsoft 365, QuickBooks, Zapier, DocuSign, WhatsApp, Zoom, Google Meet, Zoho Meeting e mais de 6.000 aplicações.",
      "intro": "O FreeCRM conecta-se às ferramentas que já utiliza — Google Workspace, Microsoft 365, QuickBooks, Zapier, DocuSign, WhatsApp, Zoom, Google Meet, Zoho Meeting e milhares de outras — para manter os seus dados sincronizados e os seus fluxos de trabalho automatizados."
    },
    "changelog": {
      "title": "Novidades no FreeCRM",
      "eyebrow": "Registo de alterações",
      "description": "Melhorias recentes, novas funcionalidades e correções lançadas no FreeCRM.",
      "intro": "Lançamos melhorias continuamente. Aqui estão os destaques dos últimos lançamentos.",
      "sections": [
        {
          "heading": "Maio de 2026 — Power dialer 2.0",
          "body": "Marcação de presença local mais rápida, envio inteligente de voicemail e atalhos de disposição de chamada que reduzem o trabalho pós-chamada para metade."
        },
        {
          "heading": "Abril de 2026 — Novas aplicações iOS e Android",
          "body": "Aplicações móveis completamente novas com suporte offline, notificações push para chamadas e SMS recebidos, e acesso total ao pipeline a partir do seu telemóvel."
        },
        {
          "heading": "Março de 2026 — Sincronização bidirecional com QuickBooks",
          "body": "Clientes, faturas e pagamentos agora sincronizam bidirecionalmente entre o FreeCRM e o QuickBooks Online e Desktop."
        },
        {
          "heading": "Fevereiro de 2026 — Acompanhamento de chamadas (coaching)",
          "body": "Controlos de escuta ao vivo, sussurro e intervenção para gestores, além de pontuação de chamadas gerada por IA no plano Enterprise."
        }
      ]
    },
    "about": {
      "title": "Sobre a FreeCRM",
      "eyebrow": "Empresa",
      "description": "Desde 2003, a FreeCRM oferece o primeiro CRM do mundo totalmente gratuito, sendo pioneira no modelo freemium para equipas de vendas, serviços e marketing.",
      "intro": "Porque é que o FreeCRM é gratuito? Porque acreditamos no seu negócio. Oferecemos o primeiro CRM do mundo totalmente gratuito desde 2003 — e já ajudámos centenas de milhares de empresas a crescer.",
      "sections": [
        {
          "heading": "Porquê gratuito?",
          "body": "Oferecemos-lhe um CRM gratuito como um investimento em si e no seu negócio. Sabemos que o ajudaremos a ter sucesso e que, algures no futuro, poderá pagar-nos. À medida que as suas vendas crescem, vai querer mais funcionalidades — e os planos Pro e Enterprise estarão lá quando estiver pronto."
        },
        {
          "heading": "Fomos pioneiros no freemium",
          "body": "A FreeCRM foi pioneira no modelo freemium: software poderoso e gratuito, com a opção de atualizar para SMS, chamadas de voz, QuickBooks, acesso avançado à API, Zapier e muito mais."
        },
        {
          "heading": "Construído na AWS",
          "body": "Levamos a segurança dos dados a sério. O FreeCRM funciona na Amazon AWS com um objetivo de disponibilidade de seis noves (99,9999%). Os engenheiros estão de serviço 24/7/365, e as nossas instalações cumprem ou excedem os requisitos federais e internacionais de segurança, redundância e continuidade de dados."
        },
        {
          "heading": "Abrangente por design",
          "body": "Um CRM robusto é uma aplicação de trabalho abrangente que gere tanto as operações de front-office como de back-office. O FreeCRM é assim poderoso — e gratuito — cobrindo vendas, serviços, marketing, chamadas e operações num só lugar."
        }
      ]
    },
    "customers": {
      "title": "Os clientes adoram o FreeCRM",
      "eyebrow": "Clientes",
      "description": "Veja como equipas de vendas outbound, call centers e PMEs usam o FreeCRM para fazer 40% mais chamadas e fechar negócios mais rapidamente.",
      "intro": "Centenas de milhares de equipas — desde startups de duas pessoas a call centers de 50 lugares — executam as suas vendas e serviços no FreeCRM.",
      "sections": [
        {
          "heading": "Vendas internas — 40% mais marcações",
          "body": "\"Os nossos SDRs passaram de 80 marcações por dia para mais de 110 assim que mudámos para o FreeCRM. O power dialer e o click-to-call dentro da vista de contacto mudaram tudo.\" — VP de Vendas, B2B SaaS"
        },
        {
          "heading": "Call centers — substituímos uma stack de $18k/ano",
          "body": "\"Substituímos o nosso marcador, CRM e ferramentas de SMS pelo FreeCRM e poupámos $18k por ano — sem perder uma única funcionalidade que os nossos representantes valorizavam.\" — Diretor de Operações, Seguros"
        },
        {
          "heading": "PME — migrámos 50 lugares",
          "body": "\"Implementámos o FreeCRM para 50 agentes em duas semanas. O provisionamento de números locais e a gravação de chamadas estavam ativos desde o primeiro dia.\" — Chefe de Receita, Serviços Domésticos"
        },
        {
          "heading": "Organização sem fins lucrativos — gestão de doadores",
          "body": "\"O FreeCRM permite-nos tratar os doadores como contactos e as promessas como negócios. O preço é justo, e as funcionalidades de chamada são revolucionárias.\" — Diretor Executivo, NPO"
        }
      ]
    },
    "careers": {
      "title": "Carreiras na FreeCRM",
      "eyebrow": "Carreiras",
      "description": "Junte-se a uma equipa remote-first que está a construir o CRM gratuito mais popular do mundo com chamadas e SMS integrados.",
      "intro": "Somos uma equipa remote-first a construir o CRM gratuito mais popular do mundo. Se adora software simples, iteração rápida e clientes que realmente usam o que constrói, gostaríamos de falar consigo.",
      "sections": [
        {
          "heading": "Como trabalhamos",
          "body": "Equipas pequenas, async-first, written-first. Lançamos novidades todas as semanas. Contratamos profissionais seniores, confiamos nas pessoas para assumirem o seu trabalho e pagamos de forma competitiva em dinheiro e ações."
        },
        {
          "heading": "Vagas em aberto",
          "body": ""
        },
        {
          "heading": "Como se candidatar",
          "body": "Envie um e-mail para careers@freecrm.com com a vaga em que está interessado, uma breve nota sobre o porquê e links para o seu trabalho. Respondemos a todas as candidaturas."
        }
      ],
      "roles": [
        "Engenheiro/a Full-stack Sénior (React / Node)",
        "Engenheiro/a de Telefonia (WebRTC, SIP)",
        "Designer de Produto",
        "Sucesso do Cliente — Vendas Outbound",
        "Executivo/a de Contas — PME"
      ]
    },
    "contact": {
      "title": "Contactar a FreeCRM",
      "eyebrow": "Contacto",
      "description": "Fale com as vendas, obtenha suporte de produto ou contacte as nossas equipas de privacidade и segurança.",
      "intro": "Estamos aqui para ajudar. Contacte a equipa certa abaixo — geralmente respondemos dentro de um dia útil.",
      "sections": [
        {
          "heading": "Vendas",
          "body": "Deseja uma demonstração dos planos Pro ou Enterprise, ou ajuda para transferir números e migrar do HubSpot? Envie um e-mail para sales@freecrm.com."
        },
        {
          "heading": "Suporte",
          "body": "Já é cliente? Abra um ticket a partir da aplicação ou envie um e-mail para support@freecrm.com. Os clientes Pro e Enterprise têm resposta prioritária."
        },
        {
          "heading": "Privacidade e segurança",
          "body": "Questões de privacidade: privacy@freecrm.com. Divulgações de segurança: security@freecrm.com. Confirmamos a receção dos relatórios em 24 horas."
        },
        {
          "heading": "Morada para correspondência",
          "body": "FreeCRM, Inc. — Customer Communications — Delaware, USA"
        }
      ]
    },
    "blog": {
      "title": "O blogue da FreeCRM",
      "eyebrow": "Blogue",
      "description": "Playbooks, análises aprofundadas de produtos e histórias de clientes para equipas de vendas outbound e call centers.",
      "intro": "Playbooks práticos para vendas outbound, vendas internas e operações de call center para PMEs — escritos pela equipa que constrói o FreeCRM.",
      "sections": [
        {
          "heading": "Como configurar um power dialer em 10 minutos",
          "body": "Um guia passo a passo para provisionar números, importar uma lista e executar a sua primeira sessão de power dialer dentro do FreeCRM."
        },
        {
          "heading": "5 formas como o FreeCRM supera o HubSpot em chamadas",
          "body": "Voz e SMS integrados, sem margem por minuto, gratuito para sempre — as funcionalidades de chamada do FreeCRM em comparação com o Sales Hub do HubSpot."
        },
        {
          "heading": "De folhas de cálculo para um CRM numa tarde",
          "body": "Como importar contactos, configurar o seu pipeline e começar a acompanhar negócios no FreeCRM numa única tarde."
        },
        {
          "heading": "Construir um call center de 50 lugares na FreeCRM",
          "body": "Como uma empresa de serviços domésticos implementou o FreeCRM para 50 agentes em duas semanas — incluindo provisionamento de números locais e gravação de chamadas."
        }
      ]
    },
    "help": {
      "title": "Centro de Ajuda",
      "eyebrow": "Recursos",
      "description": "Guias, tutoriais e respostas para tirar o máximo proveito do FreeCRM.",
      "intro": "Pesquise guias e tutoriais, ou navegue por tópico. Os clientes Pro e Enterprise também recebem suporte prioritário por e-mail.",
      "sections": [
        {
          "heading": "Começar",
          "body": "Crie a sua conta, convide a sua equipa, importe contactos e configure o seu pipeline. A maioria das equipas fica online em menos de uma hora."
        },
        {
          "heading": "Chamadas e SMS",
          "body": "Provisione números, configure o click-to-call, grave chamadas, use o power dialer e envie SMS gratuitos a partir de qualquer registo de contacto."
        },
        {
          "heading": "Pipelines e negócios",
          "body": "Personalize fases, crie relatórios e previsões, e acompanhe cada negócio desde a cotação até à fatura."
        },
        {
          "heading": "Integrações",
          "body": "Conecte o Google, Microsoft 365, QuickBooks, Zapier, DocuSign, Zoom, Google Meet, Zoho Meeting e mais de 6.000 aplicações. Sincronize contactos, calendário, e-mail, faturas e muito mais."
        },
        {
          "heading": "Administração e segurança",
          "body": "Gira utilizadores, funções, SSO, registos de auditoria e exportação de dados. Os clientes Enterprise também recebem provisionamento SCIM e um CSM dedicado."
        }
      ]
    },
    "apiDocs": {
      "title": "API do CRM",
      "eyebrow": "Documentação da API",
      "description": "Uma API REST baseada em padrões para capturar leads, sincronizar dados e automatizar vendas e suporte inbound dentro do FreeCRM.",
      "intro": "Tire o máximo proveito do seu CRM e automatize as vendas inbound e o apoio ao cliente. Os nossos Serviços Web API capacitam o seu website para enviar e receber dados diretamente no FreeCRM.",
      "whatHeading": "O que pode fazer",
      "capturesHeading": "Capturar leads do website",
      "authHeading": "Autenticação",
      "webhooksHeading": "Webhooks",
      "rateLimitsHeading": "Limites de taxa e SDKs",
      "sections": [
        {
          "heading": "O que pode fazer",
          "body": "Conecte formulários de website, encaminhe e-mails recebidos, abra casos de suporte, capture leads e sincronize dados com qualquer sistema externo. Usamos uma API REST baseada em padrões que é fácil de integrar a partir de qualquer tecnologia."
        },
        {
          "heading": "Capturar leads do website",
          "body": "Adicione o nosso modelo de código a qualquer formulário web e comece a capturar leads de suporte ao cliente e vendas em tempo real, diretamente dentro do FreeCRM. Reduza o ruído de e-mails recebidos e nunca perca um lead."
        },
        {
          "heading": "Webhooks",
          "body": "Subscreva eventos — contact.created, deal.updated, call.completed, sms.received — e envie-os para os seus próprios sistemas em tempo real."
        },
        {
          "heading": "Limites de taxa e SDKs",
          "body": "1.000 pedidos/minuto no Pro, 5.000/minuto no Enterprise. Estão disponíveis SDKs oficiais para Node e Python; bibliotecas da comunidade cobrem Ruby, PHP e Go."
        }
      ]
    },
    "community": {
      "title": "Comunidade FreeCRM",
      "eyebrow": "Comunidade",
      "description": "Conecte-se com milhares de utilizadores do FreeCRM, partilhe playbooks e obtenha respostas da equipa.",
      "intro": "Junte-se a milhares de utilizadores do FreeCRM que partilham playbooks, automações e melhores práticas para operações de vendas outbound и call center.",
      "sections": [
        {
          "heading": "Fóruns",
          "body": "Faça perguntas, partilhe o que está a funcionar e aprenda com equipas que utilizam o FreeCRM em todas as dimensões — desde fundadores a solo a call centers de 50 lugares."
        },
        {
          "heading": "Slack e Discord",
          "body": "Canais em tempo real para utilizadores avançados, administradores e programadores que constroem sobre a API do FreeCRM. A equipa de produto também está por lá."
        },
        {
          "heading": "Horário de atendimento",
          "body": "Sessões semanais ao vivo que cobrem a integração, o power dialing, as integrações e as novas funcionalidades. Gratuito para todos os clientes."
        }
      ]
    },
    "privacy": {
      "title": "Política de Privacidade",
      "eyebrow": "Legal",
      "description": "Política de privacidade da FreeCRM: os seus dados são sempre privados e nunca partilhados com terceiros.",
      "intro": "Os seus dados são sempre privados. Não partilhamos dados de clientes com terceiros.",
      "sections": [
        {
          "heading": "Recolha e utilização de informações",
          "body": "Os seus dados são 100% privados. Não utilizamos os dados que insere no sistema para qualquer outro fim que não seja o suporte ao seu CRM. Os dados que insere permanecerão propriedade exclusiva dos indivíduos e organizações que os inseriram. Estes dados nunca são partilhados com organizações externas ou terceiros. Em caso de aquisição, insolvência, mudança de propriedade ou qualquer alteração na estrutura corporativa, garantiremos sempre que os seus dados permaneçam privados e não comprometidos."
        },
        {
          "heading": "O que recolhemos",
          "body": "Recolhemos informações pessoalmente identificáveis necessárias para fornecer o serviço: nome, e-mail, telefone, detalhes de faturação e os dados que escolhe armazenar no seu CRM. Recolhemos dados de registo e de utilização para operar, proteger e melhorar o serviço."
        },
        {
          "heading": "Como utilizamos as informações",
          "body": "Utilizamos as informações para fornecer e melhorar o serviço, comunicar consigo, prestar suporte, proteger a plataforma e cumprir obrigações legais. Nunca vendemos dados pessoais."
        },
        {
          "heading": "Partilha",
          "body": "Não partilhamos nem vendemos informações de utilizadores. Contratamos subprocessadores de confiança (como os nossos fornecedores de nuvem e telefonia) estritamente para operar o serviço, sob contratos que exigem confidencialidade e proteção de dados."
        },
        {
          "heading": "Escolhas e correções",
          "body": "Pode aceder, corrigir, exportar ou apagar os seus dados a qualquer momento a partir da aplicação, ou contactando privacy@freecrm.com."
        },
        {
          "heading": "Segurança",
          "body": "A FreeCRM opera na Amazon AWS com controlos de segurança físicos, de rede e de aplicação padrão da indústria, encriptação em trânsito e em repouso, e monitorização 24/7/365."
        },
        {
          "heading": "Contacto",
          "body": "Dúvidas ou preocupações? Envie um e-mail para privacy@freecrm.com. Responderemos num prazo razoável."
        }
      ]
    },
    "terms": {
      "title": "Termos de Serviço",
      "eyebrow": "Legal",
      "description": "O Acordo de Subscrição que rege a sua utilização do software e serviços da FreeCRM.",
      "intro": "Ao utilizar o software FreeCRM, concorda, em nome da entidade que licencia o software, em ficar vinculado por este Acordo de Subscrição.",
      "sections": [
        {
          "heading": "1. Aceitação",
          "body": "Ao clicar em \"Aceito\" ou ao utilizar o software FreeCRM que selecionou, está a concordar, em nome da entidade que licencia o software (\"Empresa\"), que a Empresa se torna parte deste Acordo e que tem autoridade para vincular a Empresa. Se a Empresa não concordar, não se registe nem utilize o software."
        },
        {
          "heading": "2. Descrição do serviço",
          "body": "A FreeCRM presta o seu serviço sujeito a estes Termos de Serviço, que podem ser atualizados por nós periodicamente. A versão atual está sempre publicada neste URL. Funcionalidades específicas podem estar sujeitas a diretrizes adicionais publicadas, que são incorporadas por referência."
        },
        {
          "heading": "3. A sua conta",
          "body": "É responsável por manter a confidencialidade das suas credenciais e por toda a atividade que ocorre na sua conta. Notifique-nos imediatamente de qualquer uso não autorizado."
        },
        {
          "heading": "4. Utilização aceitável",
          "body": "Concorda em não utilizar a FreeCRM para enviar spam, assediar outros, infringir propriedade intelectual, distribuir malware ou violar qualquer lei aplicável. Podemos suspender contas que violem estas regras."
        },
        {
          "heading": "5. Taxas",
          "body": "O plano Gratuito é oferecido sem custos. Os planos Pro e Enterprise são faturados antecipadamente, mensal ou anualmente. A utilização de telefonia (números, minutos, SMS) é faturada de acordo com as taxas publicadas."
        },
        {
          "heading": "6. Propriedade dos dados",
          "body": "Mantém todos os direitos sobre os dados que insere na FreeCRM. Processamo-los apenas para fornecer e melhorar o serviço."
        },
        {
          "heading": "7. Rescisão",
          "body": "Qualquer uma das partes pode rescindir a qualquer momento. Na rescisão, poderá exportar os seus dados; após um período razoável, poderemos eliminá-los."
        },
        {
          "heading": "8. Exoneração de responsabilidade e limitação de responsabilidade",
          "body": "O serviço é fornecido \"tal como está\", sem garantias de qualquer tipo. Na medida máxima permitida por lei, a responsabilidade agregada da FreeCRM limita-se às taxas pagas nos doze meses anteriores à reclamação."
        },
        {
          "heading": "9. Lei aplicável",
          "body": "Este Acordo é regido pelas leis do Estado de Delaware, EUA, sem consideração pelos princípios de conflitos de leis."
        }
      ]
    },
    "security": {
      "title": "Segurança",
      "eyebrow": "Confiança",
      "description": "Como a FreeCRM mantém os seus dados seguros — construído na Amazon AWS com encriptação, monitorização 24/7 e controlos de acesso rigorosos.",
      "intro": "Levamos a segurança dos dados a sério. A FreeCRM opera na Amazon AWS com um objetivo de disponibilidade de seis noves (99.9999%) e engenheiros de serviço 24/7/365.",
      "sections": [
        {
          "heading": "Infraestrutura",
          "body": "A FreeCRM funciona na Amazon AWS. As nossas instalações físicas cumprem ou excedem todos os requisitos federais e internacionais de segurança de dados, redundância, recuperação de negócios e continuidade."
        },
        {
          "heading": "Encriptação",
          "body": "Todos os dados são encriptados em trânsito usando TLS 1.2+ e em repouso usando AES-256. As cópias de segurança são encriptadas e armazenadas em várias zonas de disponibilidade."
        },
        {
          "heading": "Controlos de acesso",
          "body": "Acesso baseado em funções na aplicação, princípio do menor privilégio para acesso à produção e registo de auditoria de ações administrativas. Os clientes Enterprise obtêm SSO (SAML), provisionamento SCIM e registos de auditoria completos."
        },
        {
          "heading": "Monitorização e resposta",
          "body": "Monitorização da plataforma 24/7/365, alertas automatizados e uma equipa de engenharia de prevenção. Testamos as cópias de segurança regularmente e realizamos exercícios periódicos de resposta a incidentes."
        },
        {
          "heading": "Relatar uma vulnerabilidade",
          "body": "Envie um e-mail para security@freecrm.com com os detalhes. Confirmamos a receção dos relatórios em 24 horas e creditamos a divulgação responsável."
        }
      ]
    },
    "dpa": {
      "title": "Adendo de Processamento de Dados",
      "eyebrow": "Legal",
      "description": "O DPA regula como a FreeCRM processa dados pessoais em nome dos clientes, em conformidade com o GDPR e leis semelhantes.",
      "intro": "Este Adendo de Processamento de Dados (DPA) complementa o seu Acordo de Subscrição da FreeCRM e regula como processamos dados pessoais em seu nome.",
      "sections": [
        {
          "heading": "1. Funções",
          "body": "Você (Cliente) é o controlador de dados. A FreeCRM atua como processador de dados e processa dados pessoais apenas sob as suas instruções documentadas, incluindo transferências, exceto quando exigido pela lei aplicável."
        },
        {
          "heading": "2. Objeto e duração",
          "body": "Objeto: prestação do serviço FreeCRM. Duração: o termo do Acordo mais qualquer período de retenção pós-rescisão."
        },
        {
          "heading": "3. Subprocessadores",
          "body": "Contratamos subprocessadores verificados (incluindo a AWS para alojamento e os nossos fornecedores de telefonia para voz e SMS). Uma lista atual está disponível mediante pedido. Notificamos os clientes sobre alterações materiais."
        },
        {
          "heading": "4. Medidas de segurança",
          "body": "Implementamos medidas técnicas e organizacionais apropriadas ao risco, incluindo encriptação em trânsito e em repouso, controlos de acesso, registo, gestão de vulnerabilidades e formação de pessoal."
        },
        {
          "heading": "5. Direitos dos titulares dos dados",
          "body": "Ajudamo-lo a cumprir os pedidos dos titulares dos dados (acesso, retificação, apagamento, portabilidade) através de ferramentas na aplicação e, quando necessário, através de suporte."
        },
        {
          "heading": "6. Transferências internacionais",
          "body": "Quando os dados pessoais são transferidos para fora do EEE, Reino Unido ou Suíça, a transferência é regida pelas Cláusulas Contratuais Padrão ou por outro mecanismo de transferência legal."
        },
        {
          "heading": "7. Notificação de violação",
          "body": "Iremos notificá-lo sem demora indevida após tomarmos conhecimento de uma violação de dados pessoais que afete os seus dados."
        },
        {
          "heading": "8. Auditorias",
          "body": "Com um aviso razoável, disponibilizaremos as informações necessárias para demonstrar a conformidade, incluindo relatórios de auditoria de terceiros, quando aplicável."
        }
      ]
    },
    "industriesIndex": {
      "title": "FreeCRM por setor",
      "eyebrow": "Setores",
      "description": "Veja como equipas em call centers, seguros, imobiliário, odontologia, serviços profissionais e muito mais, usam o FreeCRM.",
      "intro": "Modelos e fluxos de trabalho para os setores que funcionam com o FreeCRM."
    }
  }
};

export default pt;
